import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { supabase, Tables } from "../utils/supabase";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  subMonths,
  subQuarters,
  parseISO,
} from "date-fns";
import { nl } from "date-fns/locale";

type Period = "month" | "quarter";

interface ReportData {
  period: { from: string; to: string; label: string };
  pharmacy: { name: string; address?: string };
  tasks: { total: number; completed: number; cancelled: number; overdue: number };
  complaints: { total: number; resolved: number; open: number; capa_open: number };
  calibrations: { total: number; passed: number; needs_adjustment: number; failed: number };
  preparations: { total: number; validated: number; pending: number };
  expiry: { expired: number; near_expiry: number; hazardous_expired: number };
}

const QaReport: React.FC = () => {
  const { selectedPharmacy } = useAuth();
  const [period, setPeriod] = useState<Period>("month");
  const [offset, setOffset] = useState(0); // 0 = current, 1 = previous, etc.
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);

  const getPeriodRange = () => {
    const now = new Date();
    if (period === "month") {
      const base = subMonths(now, offset);
      return { from: startOfMonth(base), to: endOfMonth(base) };
    } else {
      const base = subQuarters(now, offset);
      return { from: startOfQuarter(base), to: endOfQuarter(base) };
    }
  };

  const getPeriodLabel = () => {
    const { from, to } = getPeriodRange();
    if (period === "month") return format(from, "MMMM yyyy", { locale: nl });
    return `Q${Math.ceil((from.getMonth() + 1) / 3)} ${format(from, "yyyy")}`;
  };

  useEffect(() => {
    if (selectedPharmacy) fetchReportData();
  }, [selectedPharmacy, period, offset]);

  const fetchReportData = async () => {
    if (!selectedPharmacy) return;
    setLoading(true);
    try {
      const { from, to } = getPeriodRange();
      const fromISO = from.toISOString();
      const toISO = to.toISOString();
      const fromDate = format(from, "yyyy-MM-dd");
      const toDate = format(to, "yyyy-MM-dd");

      const [tasksRes, complaintsRes, calibRes, prepRes, expiryRes] = await Promise.all([
        supabase.from(Tables.TASKS).select("status, due_date, completed_at").eq("pharmacy_id", selectedPharmacy.id).gte("created_at", fromISO).lte("created_at", toISO),
        supabase.from(Tables.COMPLAINTS).select("status, resolved_at, capa_status").eq("pharmacy_id", selectedPharmacy.id).gte("created_at", fromISO).lte("created_at", toISO),
        supabase.from(Tables.CALIBRATION_RECORDS).select("status").eq("pharmacy_id", selectedPharmacy.id).gte("calibration_date", fromDate).lte("calibration_date", toDate),
        supabase.from(Tables.PREPARATIONS).select("status").eq("pharmacy_id", selectedPharmacy.id).gte("created_at", fromISO).lte("created_at", toISO),
        supabase.from(Tables.COMPOUNDING_MATERIALS_EXPIRY).select("status, expiry_date, is_hazardous").eq("pharmacy_id", selectedPharmacy.id),
      ]);

      const tasks = tasksRes.data ?? [];
      const complaints = complaintsRes.data ?? [];
      const calibrations = calibRes.data ?? [];
      const preparations = prepRes.data ?? [];
      const expiry = expiryRes.data ?? [];
      const today = new Date();

      setReportData({
        period: { from: format(from, "dd/MM/yyyy"), to: format(to, "dd/MM/yyyy"), label: getPeriodLabel() },
        pharmacy: { name: selectedPharmacy.name },
        tasks: {
          total: tasks.length,
          completed: tasks.filter((t) => t.status === "completed").length,
          cancelled: tasks.filter((t) => t.status === "cancelled").length,
          overdue: tasks.filter((t) => t.status !== "completed" && t.status !== "cancelled" && t.due_date && new Date(t.due_date) < today).length,
        },
        complaints: {
          total: complaints.length,
          resolved: complaints.filter((c) => c.status === "resolved" || c.status === "closed").length,
          open: complaints.filter((c) => c.status === "open" || c.status === "in_progress").length,
          capa_open: complaints.filter((c) => c.capa_status === "open" || c.capa_status === "in_progress").length,
        },
        calibrations: {
          total: calibrations.length,
          passed: calibrations.filter((c) => c.status === "passed").length,
          needs_adjustment: calibrations.filter((c) => c.status === "needs_adjustment").length,
          failed: calibrations.filter((c) => c.status === "failed").length,
        },
        preparations: {
          total: preparations.length,
          validated: preparations.filter((p) => p.status === "validated" || p.status === "approved").length,
          pending: preparations.filter((p) => p.status === "pending" || p.status === "in_progress").length,
        },
        expiry: {
          expired: expiry.filter((e) => e.expiry_date && new Date(e.expiry_date) < today).length,
          near_expiry: expiry.filter((e) => { if (!e.expiry_date) return false; const d = new Date(e.expiry_date); return d >= today && d <= new Date(today.getTime() + 90 * 86400000); }).length,
          hazardous_expired: expiry.filter((e) => e.is_hazardous && e.expiry_date && new Date(e.expiry_date) < today).length,
        },
      });
    } catch (err) {
      console.error("QA report fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (!reportData) return;
    const d = reportData;
    const taskPct = d.tasks.total > 0 ? Math.round((d.tasks.completed / d.tasks.total) * 100) : null;
    const calPct = d.calibrations.total > 0 ? Math.round((d.calibrations.passed / d.calibrations.total) * 100) : null;
    const complaintPct = d.complaints.total > 0 ? Math.round((d.complaints.resolved / d.complaints.total) * 100) : null;
    const prepPct = d.preparations.total > 0 ? Math.round((d.preparations.validated / d.preparations.total) * 100) : null;

    const row = (label: string, value: string | number, sub?: string, highlight?: "green" | "red" | "amber") => {
      const color = highlight === "green" ? "#16a34a" : highlight === "red" ? "#dc2626" : highlight === "amber" ? "#d97706" : "#111827";
      return `<tr><td style="padding:6px 12px;border-bottom:1px solid #f3f4f6;color:#6b7280;font-size:13px;">${label}</td><td style="padding:6px 12px;border-bottom:1px solid #f3f4f6;font-weight:600;color:${color};font-size:13px;">${value}${sub ? `<span style="font-weight:400;color:#9ca3af;font-size:11px;margin-left:6px;">${sub}</span>` : ""}</td></tr>`;
    };

    const section = (title: string, rows: string) =>
      `<div style="margin-bottom:28px;break-inside:avoid;"><h3 style="font-size:13px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#4b5563;border-bottom:2px solid #e5e7eb;padding-bottom:6px;margin-bottom:0;">${title}</h3><table style="width:100%;border-collapse:collapse;">${rows}</table></div>`;

    const html = `<!DOCTYPE html><html lang="nl"><head><meta charset="UTF-8"><title>QA Rapport – ${d.period.label}</title>
    <style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;margin:0;padding:32px;color:#111827;}
    @media print{body{padding:0;}button{display:none;}}</style></head>
    <body>
    <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:32px;padding-bottom:16px;border-bottom:3px solid #1d4ed8;">
      <div><h1 style="font-size:22px;font-weight:800;color:#1d4ed8;margin:0 0 4px 0;">Kwaliteitsrapport</h1>
      <p style="margin:0;font-size:15px;font-weight:600;color:#111827;">${d.pharmacy.name}</p>
      <p style="margin:4px 0 0;font-size:13px;color:#6b7280;">Periode: ${d.period.from} – ${d.period.to}</p></div>
      <div style="text-align:right;"><p style="margin:0;font-size:12px;color:#9ca3af;">Gegenereerd op</p>
      <p style="margin:0;font-size:13px;font-weight:600;">${format(new Date(), "dd/MM/yyyy HH:mm")}</p></div>
    </div>

    <div style="display:grid;grid-template-columns:1fr 1fr;gap:24px;">
    <div>
    ${section("Taken", [
      row("Aangemaakt in periode", d.tasks.total),
      row("Voltooid", d.tasks.completed, taskPct !== null ? `${taskPct}%` : undefined, taskPct !== null ? (taskPct >= 80 ? "green" : taskPct >= 50 ? "amber" : "red") : undefined),
      row("Geannuleerd", d.tasks.cancelled),
      row("Verlopen (niet voltooid)", d.tasks.overdue, undefined, d.tasks.overdue > 0 ? "red" : "green"),
    ].join(""))}
    ${section("Klachten", [
      row("Aangemaakt in periode", d.complaints.total),
      row("Opgelost / Gesloten", d.complaints.resolved, complaintPct !== null ? `${complaintPct}%` : undefined, complaintPct !== null ? (complaintPct >= 80 ? "green" : "amber") : undefined),
      row("Openstaand", d.complaints.open, undefined, d.complaints.open > 0 ? "amber" : "green"),
      row("CAPA openstaand", d.complaints.capa_open, undefined, d.complaints.capa_open > 0 ? "red" : "green"),
    ].join(""))}
    </div>
    <div>
    ${section("Kalibraties", [
      row("Uitgevoerd in periode", d.calibrations.total),
      row("Geslaagd", d.calibrations.passed, calPct !== null ? `${calPct}%` : undefined, calPct !== null ? (calPct >= 90 ? "green" : calPct >= 70 ? "amber" : "red") : undefined),
      row("Aanpassing nodig", d.calibrations.needs_adjustment, undefined, d.calibrations.needs_adjustment > 0 ? "amber" : undefined),
      row("Niet geslaagd", d.calibrations.failed, undefined, d.calibrations.failed > 0 ? "red" : undefined),
    ].join(""))}
    ${section("Magistrale bereidingen", [
      row("Aangemaakt in periode", d.preparations.total),
      row("Gevalideerd", d.preparations.validated, prepPct !== null ? `${prepPct}%` : undefined, prepPct !== null ? (prepPct >= 90 ? "green" : "amber") : undefined),
      row("In behandeling / wachtend", d.preparations.pending, undefined, d.preparations.pending > 0 ? "amber" : undefined),
    ].join(""))}
    ${section("Grondstoffen vervaldatums", [
      row("Vervaldatum verstreken", d.expiry.expired, undefined, d.expiry.expired > 0 ? "red" : "green"),
      row("Verlopen binnen 90 dagen", d.expiry.near_expiry, undefined, d.expiry.near_expiry > 0 ? "amber" : undefined),
      row("Gevaarlijke stoffen verlopen", d.expiry.hazardous_expired, undefined, d.expiry.hazardous_expired > 0 ? "red" : "green"),
    ].join(""))}
    </div>
    </div>

    <div style="margin-top:40px;padding-top:20px;border-top:1px solid #e5e7eb;display:grid;grid-template-columns:1fr 1fr;gap:40px;">
      <div><p style="font-size:12px;color:#9ca3af;margin:0 0 40px;">Handtekening verantwoordelijke apotheker</p><div style="border-top:1px solid #374151;padding-top:4px;font-size:11px;color:#6b7280;">Naam / Datum</div></div>
      <div><p style="font-size:12px;color:#9ca3af;margin:0 0 40px;">Handtekening kwaliteitsverantwoordelijke</p><div style="border-top:1px solid #374151;padding-top:4px;font-size:11px;color:#6b7280;">Naam / Datum</div></div>
    </div>
    </body></html>`;

    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 400);
  };

  const r = reportData;

  const StatRow: React.FC<{ label: string; value: number; of?: number; color?: string }> = ({ label, value, of, color = "text-gray-900" }) => (
    <div className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-600">{label}</span>
      <span className={`text-sm font-semibold ${color}`}>
        {value}{of !== undefined && <span className="text-gray-400 font-normal text-xs ml-1">/ {of}</span>}
      </span>
    </div>
  );

  const Card: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
      <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-3">{title}</h3>
      {children}
    </div>
  );

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">QA Rapport</h1>
          <p className="text-sm text-gray-500 mt-0.5">Overzicht van kwaliteitsindicatoren per periode</p>
        </div>
        <button
          onClick={handlePrint}
          disabled={!r || loading}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm font-medium"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.056 48.056 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5zm-3 0h.008v.008H15V10.5z" /></svg>
          Afdrukken / PDF
        </button>
      </div>

      {/* Period selector */}
      <div className="flex items-center gap-3 mb-6 bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
          {(["month", "quarter"] as Period[]).map((p) => (
            <button key={p} onClick={() => { setPeriod(p); setOffset(0); }}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${period === p ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:text-gray-700"}`}>
              {p === "month" ? "Maand" : "Kwartaal"}
            </button>
          ))}
        </div>
        <button onClick={() => setOffset((o) => o + 1)} className="p-1.5 rounded hover:bg-gray-100 text-gray-500">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
        </button>
        <span className="text-sm font-semibold text-gray-900 min-w-[140px] text-center capitalize">{getPeriodLabel()}</span>
        <button onClick={() => setOffset((o) => Math.max(0, o - 1))} disabled={offset === 0} className="p-1.5 rounded hover:bg-gray-100 text-gray-500 disabled:opacity-30">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
        </button>
        {loading && <span className="text-xs text-gray-400 ml-2">Laden...</span>}
      </div>

      {r && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Card title="Taken">
            <StatRow label="Aangemaakt in periode" value={r.tasks.total} />
            <StatRow label="Voltooid" value={r.tasks.completed} of={r.tasks.total}
              color={r.tasks.total > 0 ? (r.tasks.completed / r.tasks.total >= 0.8 ? "text-green-600" : r.tasks.completed / r.tasks.total >= 0.5 ? "text-amber-600" : "text-red-600") : "text-gray-900"} />
            <StatRow label="Geannuleerd" value={r.tasks.cancelled} />
            <StatRow label="Verlopen (niet voltooid)" value={r.tasks.overdue} color={r.tasks.overdue > 0 ? "text-red-600" : "text-green-600"} />
          </Card>

          <Card title="Klachten">
            <StatRow label="Aangemaakt in periode" value={r.complaints.total} />
            <StatRow label="Opgelost / Gesloten" value={r.complaints.resolved} of={r.complaints.total}
              color={r.complaints.total > 0 ? (r.complaints.resolved / r.complaints.total >= 0.8 ? "text-green-600" : "text-amber-600") : "text-gray-900"} />
            <StatRow label="Openstaand" value={r.complaints.open} color={r.complaints.open > 0 ? "text-amber-600" : "text-green-600"} />
            <StatRow label="CAPA openstaand" value={r.complaints.capa_open} color={r.complaints.capa_open > 0 ? "text-red-600" : "text-green-600"} />
          </Card>

          <Card title="Kalibraties">
            <StatRow label="Uitgevoerd in periode" value={r.calibrations.total} />
            <StatRow label="Geslaagd" value={r.calibrations.passed} of={r.calibrations.total}
              color={r.calibrations.total > 0 ? (r.calibrations.passed / r.calibrations.total >= 0.9 ? "text-green-600" : r.calibrations.passed / r.calibrations.total >= 0.7 ? "text-amber-600" : "text-red-600") : "text-gray-900"} />
            <StatRow label="Aanpassing nodig" value={r.calibrations.needs_adjustment} color={r.calibrations.needs_adjustment > 0 ? "text-amber-600" : "text-gray-900"} />
            <StatRow label="Niet geslaagd" value={r.calibrations.failed} color={r.calibrations.failed > 0 ? "text-red-600" : "text-gray-900"} />
          </Card>

          <Card title="Magistrale bereidingen">
            <StatRow label="Aangemaakt in periode" value={r.preparations.total} />
            <StatRow label="Gevalideerd" value={r.preparations.validated} of={r.preparations.total}
              color={r.preparations.total > 0 ? (r.preparations.validated / r.preparations.total >= 0.9 ? "text-green-600" : "text-amber-600") : "text-gray-900"} />
            <StatRow label="In behandeling / wachtend" value={r.preparations.pending} color={r.preparations.pending > 0 ? "text-amber-600" : "text-gray-900"} />
          </Card>

          <Card title="Grondstoffen — vervaldatums">
            <StatRow label="Vervaldatum verstreken" value={r.expiry.expired} color={r.expiry.expired > 0 ? "text-red-600" : "text-green-600"} />
            <StatRow label="Verlopen binnen 90 dagen" value={r.expiry.near_expiry} color={r.expiry.near_expiry > 0 ? "text-amber-600" : "text-gray-900"} />
            <StatRow label="Gevaarlijke stoffen verlopen" value={r.expiry.hazardous_expired} color={r.expiry.hazardous_expired > 0 ? "text-red-600" : "text-green-600"} />
          </Card>
        </div>
      )}
    </div>
  );
};

export default QaReport;
